import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/config";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { format } from "date-fns";

const AdminDeliveryTracker = () => {
  const [date, setDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [slot, setSlot] = useState("Morning");
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const navigate = useNavigate();

  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, "deliveries"),
        where("date", "==", date),
        where("slot", "==", slot)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDeliveries(data);
    } catch (error) {
      console.error("Error loading deliveries:", error);
    } finally {
      setLoading(false);
    }
  }, [date, slot]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // ✅ 1. Update Status Function
  const updateStatus = async (deliveryId, newStatus) => {
    try {
      setUpdatingStatus(deliveryId);
      await updateDoc(doc(db, "deliveries", deliveryId), {
        status: newStatus,
        updatedAt: new Date()
      });

      // Update local state immediately for better UX
      setDeliveries(prev =>
        prev.map(d =>
          d.id === deliveryId
            ? { ...d, status: newStatus, updatedAt: new Date() }
            : d
        )
      );

      console.log(`✅ Status updated to ${newStatus} for delivery ${deliveryId}`);
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status. Please try again.");
    } finally {
      setUpdatingStatus(null);
    }
  };

  // 📤 2. Send Notification Function
  const sendNotification = async (customerName, userId) => {
    try {
      // For now, simulate notification sending
      // In production, this would call your backend/Firebase Functions
      console.log(`📤 Sending notification to ${customerName} (${userId})`);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      alert(`✅ Delivery reminder sent to ${customerName}!`);
    } catch (error) {
      console.error("Error sending notification:", error);
      alert("Failed to send notification. Please try again.");
    }
  };

  // 📥 3. Export CSV Function
  const exportCSV = () => {
    try {
      const headers = ["Customer Name", "Plan", "Address", "Status", "Date", "Slot"];
      const rows = deliveries.map(d => [
        d.customerName || "N/A",
        d.planName || "N/A",
        d.address || "N/A",
        d.status || "N/A",
        d.date || "N/A",
        d.slot || "N/A"
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers, ...rows].map(row =>
          row.map(cell => `"${cell}"`).join(",")
        ).join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `deliveries_${date}_${slot}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`📥 CSV exported: deliveries_${date}_${slot}.csv`);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  // 📅 4. View Calendar Function
  const viewCalendar = (userId, customerName) => {
    // For now, navigate to customer profile
    // In the future, this could go to a dedicated calendar view
    navigate(`/admin/customers/${userId}`);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "delivered":
        return "✔️";
      case "pending":
        return "⏳";
      case "not_delivered":
        return "❌ Not Delivered";
      default:
        return "❓";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "text-green-600";
      case "pending":
        return "text-yellow-600";
      case "not_delivered":
        return "text-red-600";
      default:
        return "text-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-7xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Dashboard
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Delivery Tracker</span>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-green-700">🚚 Delivery Tracker</h1>
          <div className="text-sm text-gray-600">
            Total Deliveries: {deliveries.length}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded-xl shadow border border-green-100">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Slot</label>
            <select
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="Morning">🌅 Morning</option>
              <option value="Evening">🌆 Evening</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setDate(format(new Date(), "yyyy-MM-dd"));
                setSlot("Morning");
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-all"
            >
              Reset to Today
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Total Deliveries</div>
            <div className="text-2xl font-bold text-green-700">{deliveries.length}</div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Delivered</div>
            <div className="text-2xl font-bold text-green-700">
              {deliveries.filter(d => d.status === 'delivered').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-2xl font-bold text-yellow-700">
              {deliveries.filter(d => d.status === 'pending').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
            <div className="text-sm text-gray-600">Not Delivered</div>
            <div className="text-2xl font-bold text-red-700">
              {deliveries.filter(d => d.status === 'not_delivered').length}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-xl overflow-hidden border border-green-100">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-green-100 text-green-800">
                <tr>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Phone</th>
                  <th className="px-4 py-3 font-semibold">Area</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Address</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center p-8">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mr-3"></div>
                        <span className="text-green-700">Loading deliveries...</span>
                      </div>
                    </td>
                  </tr>
                ) : deliveries.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center p-8 text-gray-500">
                      <div className="text-4xl mb-2">📦</div>
                      <p>No deliveries for {date} ({slot} slot)</p>
                      <p className="text-sm mt-1">Select a different date or slot to view deliveries</p>
                    </td>
                  </tr>
                ) : (
                  deliveries.map((delivery, index) => (
                    <tr key={delivery.id} className="border-b hover:bg-green-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium">{delivery.userName || delivery.customerName || delivery.userId}</div>
                        {delivery.userId && (
                          <div className="text-xs text-gray-500 font-mono">{delivery.userId}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {delivery.phone || "N/A"}
                        {delivery.phone && (
                          <a href={`tel:${delivery.phone}`} className="ml-2 text-blue-600 underline text-xs">📞 Call</a>
                        )}
                      </td>
                      <td className="px-4 py-3">{delivery.area || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{delivery.planName || "N/A"}</div>
                        {delivery.rotisPerDay && (
                          <div className="text-xs text-gray-500">{delivery.rotisPerDay} rotis</div>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="text-ellipsis overflow-hidden" title={delivery.addressDetails || delivery.address}>
                          {delivery.addressDetails || delivery.address || "N/A"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-bold ${getStatusColor(delivery.status)}`}>
                          {getStatusIcon(delivery.status)} {delivery.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">

                          {/* ✅ Inline Status Action Buttons */}
                          <div className="flex gap-1">
                            {/* ✔ Delivered */}
                            <button
                              onClick={() => updateStatus(delivery.id, "delivered")}
                              disabled={updatingStatus === delivery.id}
                              className={`px-2 py-1 rounded text-xs font-medium transition-all ${delivery.status === "delivered"
                                  ? "bg-green-100 text-green-700 border border-green-300"
                                  : "bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-700 border border-gray-300"
                                } ${updatingStatus === delivery.id ? "opacity-50 cursor-not-allowed" : ""}`}
                              title="Mark as Delivered"
                            >
                              {updatingStatus === delivery.id ? "..." : "✔"}
                            </button>

                            {/* ⏳ Pending */}
                            <button
                              onClick={() => updateStatus(delivery.id, "pending")}
                              disabled={updatingStatus === delivery.id}
                              className={`px-2 py-1 rounded text-xs font-medium transition-all ${delivery.status === "pending"
                                  ? "bg-yellow-100 text-yellow-700 border border-yellow-300"
                                  : "bg-gray-100 text-gray-600 hover:bg-yellow-100 hover:text-yellow-700 border border-gray-300"
                                } ${updatingStatus === delivery.id ? "opacity-50 cursor-not-allowed" : ""}`}
                              title="Mark as Pending"
                            >
                              {updatingStatus === delivery.id ? "..." : "⏳"}
                            </button>

                            {/* ❌ Not Delivered */}
                            <button
                              onClick={() => updateStatus(delivery.id, "not_delivered")}
                              disabled={updatingStatus === delivery.id}
                              className={`px-2 py-1 rounded text-xs font-medium transition-all ${delivery.status === "not_delivered"
                                  ? "bg-red-100 text-red-700 border border-red-300"
                                  : "bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-700 border border-gray-300"
                                } ${updatingStatus === delivery.id ? "opacity-50 cursor-not-allowed" : ""}`}
                              title="Mark as Not Delivered"
                            >
                              {updatingStatus === delivery.id ? "..." : "❌"}
                            </button>
                          </div>


                          {/* 📤 Send Notification */}
                          <button
                            onClick={() => sendNotification(delivery.customerName, delivery.userId)}
                            className="text-blue-600 hover:text-blue-800 text-xs bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded border border-blue-200 transition-all"
                            title="Send Delivery Notification"
                          >
                            📱
                          </button>

                          {/* 📅 View Calendar */}
                          <button
                            onClick={() => viewCalendar(delivery.userId, delivery.customerName)}
                            className="text-yellow-600 hover:text-yellow-800 text-xs bg-yellow-50 hover:bg-yellow-100 px-2 py-1 rounded border border-yellow-200 transition-all"
                            title="View Customer Calendar"
                          >
                            📅
                          </button>

                          {/* 👤 View Customer */}
                          <button
                            onClick={() => navigate(`/admin/customers/${delivery.userId}`)}
                            className="text-purple-600 hover:text-purple-800 text-xs bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded border border-purple-200 transition-all"
                            title="View Customer Profile"
                          >
                            👤
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-white p-4 rounded-xl shadow-sm border border-green-100">
          <h3 className="text-lg font-semibold text-green-700 mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={exportCSV}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-all flex items-center gap-2"
            >
              📊 Export Delivery Report
            </button>
            <button
              onClick={() => {
                const pendingDeliveries = deliveries.filter(d => d.status === 'pending');
                if (pendingDeliveries.length === 0) {
                  alert("No pending deliveries to notify!");
                  return;
                }
                pendingDeliveries.forEach(d => sendNotification(d.customerName, d.userId));
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2"
            >
              📱 Send Bulk Notifications
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-all flex items-center gap-2"
            >
              📅 View Calendar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDeliveryTracker; 